




































































































































































import { Component, Vue } from 'vue-property-decorator';
import { apiUrl as apiRoot } from '@/env';

import StartButton from '@/components/StartButton.vue';
import {
  dispatchCancelGoogleSearch,
  dispatchGetGoogleSearchStatus,
  dispatchGetGoogleSearchVerifications,
  dispatchRunGoogleSearch,
} from '@/store/google-search/actions';
import {
  readGoogleSearchFinished,
  readGoogleSearchState,
  readGoogleSearchVerificationHistory,
  readGoogleSearchVerificationHistoryTotal,
} from '@/store/google-search/getters';
import { commitSetGoogleSearchFinished } from '@/store/google-search/mutations';
import { commitAddNotification } from '@/store/main/mutations';
import { delay } from '@/utils';
import {
  IGoogleSearchVerificationCurrent,
  IGoogleSearchVerificationItem,
} from '@/interfaces/google-search';

const GS_STATUS_ACTIVE = 1;
const GS_POLL_QR_MS = 1000;
const GS_POLL_ACTIVE_MS = 2000;
const GS_POLL_TAIL_MS = 1500;
const GS_TAIL_POLLS = 5;

@Component({
  components: {
    StartButton,
  },
})
export default class GoogleSearch extends Vue {
  public timeOutPointer?: number = undefined;
  public apiRoot = apiRoot;
  public tailPollsLeft = 0;
  public lastVerificationFinger = '';
  public lastPendingFinger = '';

  public verificationHeaders = [
    { text: 'Статус', value: 'state' },
    { text: 'Аккаунт', value: 'account_email' },
    { text: 'Время запроса', value: 'required_at' },
    { text: 'Время подтверждения', value: 'verified_at' },
    { text: 'Длительность', value: 'duration_sec' },
    { text: 'QR', value: 'qr_image_url', sortable: false },
  ];

  get gsState() {
    return readGoogleSearchState(this.$store);
  }

  get verificationCurrent(): IGoogleSearchVerificationCurrent | null {
    return this.gsState.verification_current || null;
  }

  get verificationPending(): IGoogleSearchVerificationCurrent[] {
    const pending = this.gsState.verification_pending || [];
    return pending.filter(
      (item) => item && item.state === 'qr_required' && item.qr_image_url,
    );
  }

  get verificationHistory(): IGoogleSearchVerificationItem[] {
    return readGoogleSearchVerificationHistory(this.$store);
  }

  get verificationHistoryTotal() {
    return readGoogleSearchVerificationHistoryTotal(this.$store);
  }

  get isFinished() {
    return readGoogleSearchFinished(this.$store);
  }

  get showVerificationPanel() {
    if (this.verificationPending.length) {
      return true;
    }
    const cur = this.verificationCurrent;
    return Boolean(cur && cur.state !== 'idle');
  }

  get verificationAlertType() {
    if (this.verificationPending.length) {
      return 'warning';
    }
    const state = this.verificationCurrent
      && this.verificationCurrent.state;
    if (state === 'verified') return 'success';
    if (state === 'timeout' || state === 'failed') return 'error';
    return 'warning';
  }

  get verificationTitle() {
    const n = this.verificationPending.length;
    if (n > 1) {
      return `Нужно отсканировать ${n} QR (по очереди)`;
    }
    if (n === 1) {
      const email = this.verificationPending[0].account_email;
      return email
        ? `Требуется сканирование QR-кода (${email})`
        : 'Требуется сканирование QR-кода';
    }
    const state = this.verificationCurrent
      && this.verificationCurrent.state;
    if (state === 'verified') {
      return 'Претензия отправлена';
    }
    if (state === 'timeout') {
      return 'Претензия не отправлена (таймаут QR)';
    }
    if (state === 'failed') {
      return 'Претензия не отправлена';
    }
    return 'Требуется сканирование QR-кода';
  }

  get logPanelText() {
    const lines = this.gsState.logs;
    if (lines && lines.length) {
      return lines.join('\n');
    }
    return 'Нет строк журнала (ещё).';
  }

  get pollIntervalMs() {
    if (this.verificationPending.length) {
      return GS_POLL_QR_MS;
    }
    if (this.gsState.status === GS_STATUS_ACTIVE) {
      return GS_POLL_ACTIVE_MS;
    }
    if (this.tailPollsLeft > 0) {
      return GS_POLL_TAIL_MS;
    }
    return GS_POLL_ACTIVE_MS;
  }

  public pendingItemKey(
    item: IGoogleSearchVerificationCurrent,
    index: number,
  ) {
    return [
      index,
      item.account_email || '',
      item.required_at || '',
      item.qr_filename || '',
      item.updated_at || '',
    ].join('|');
  }

  public pendingQrUrl(item: IGoogleSearchVerificationCurrent) {
    if (!item.qr_image_url) {
      return '';
    }
    const base = this.fullQrUrl(item.qr_image_url);
    const bust = encodeURIComponent(
      item.updated_at
      || item.required_at
      || item.qr_filename
      || String(Date.now()),
    );
    const sep = base.indexOf('?') >= 0 ? '&' : '?';
    return `${base}${sep}v=${bust}`;
  }

  public pendingFinger(
    items: IGoogleSearchVerificationCurrent[],
  ) {
    return items.map(
      (item) => [
        item.account_email || '',
        item.required_at || '',
        item.qr_filename || '',
        item.updated_at || '',
      ].join(':'),
    ).join(';');
  }

  public verificationFinger(
    cur: IGoogleSearchVerificationCurrent | null,
  ) {
    if (!cur || cur.state === 'idle') {
      return '';
    }
    return [
      cur.state,
      cur.account_email || '',
      cur.required_at || '',
      cur.verified_at || '',
      cur.updated_at || '',
      cur.qr_filename || '',
    ].join('|');
  }

  public notifyVerifiedIfNeeded(
    prevFinger: string,
    next: IGoogleSearchVerificationCurrent | null,
  ) {
    if (!prevFinger || !next) {
      return;
    }
    const email = next.account_email
      ? ` (${next.account_email})`
      : '';
    if (next.state === 'verified') {
      commitAddNotification(this.$store, {
        content: `Google Search: претензия отправлена${email}`,
        color: 'success',
      });
      return;
    }
    if (next.state === 'failed' || next.state === 'timeout') {
      commitAddNotification(this.$store, {
        content: `Google Search: претензия не отправлена${email}`,
        color: 'error',
      });
    }
  }

  public async syncVerificationChange(
    next: IGoogleSearchVerificationCurrent | null,
    pending: IGoogleSearchVerificationCurrent[],
  ) {
    const pendingFinger = this.pendingFinger(pending);
    if (pendingFinger !== this.lastPendingFinger) {
      this.lastPendingFinger = pendingFinger;
      await this.loadVerificationHistory();
    }
    const finger = this.verificationFinger(next);
    if (!finger || finger === this.lastVerificationFinger) {
      return;
    }
    const prev = this.lastVerificationFinger;
    this.lastVerificationFinger = finger;
    this.notifyVerifiedIfNeeded(prev, next);
    await this.loadVerificationHistory();
  }

  public async handleSendClaims() {
    this.tailPollsLeft = 0;
    await dispatchRunGoogleSearch(this.$store);
    this.checkGoogleSearchStatus();
  }

  public setFinished(value = true) {
    commitSetGoogleSearchFinished(this.$store, value);
  }

  public async loadVerificationHistory() {
    await dispatchGetGoogleSearchVerifications(this.$store);
  }

  public scheduleNextPoll() {
    clearTimeout(this.timeOutPointer);
    this.timeOutPointer = setTimeout(
      () => this.checkGoogleSearchStatus(),
      this.pollIntervalMs,
    );
  }

  public finishTaskBanner(
    cancelled: boolean,
    data: { total: number | null; done: number | null },
  ) {
    const statusMsg = this.gsState.message
      ? `. ${this.gsState.message}`
      : '';
    const statMsg = data.total
      ? `подано ${data.done} претензий из ${data.total}`
      : '';
    const msg = statMsg + statusMsg;
    const content = cancelled
      ? 'Google Search: задача отменена'
        + (msg ? `; ${msg}` : '')
      : `Google Search: ${msg}`;
    commitAddNotification(this.$store, { content, color: 'info' });
    this.setFinished(false);
  }

  public async checkGoogleSearchStatus(cancelled = false) {
    const data = await dispatchGetGoogleSearchStatus(this.$store);
    if (!data) {
      return;
    }
    const pending = (data.verification_pending || []).filter(
      (item: IGoogleSearchVerificationCurrent) => (
        item
        && item.state === 'qr_required'
        && item.qr_image_url
      ),
    );
    await this.syncVerificationChange(
      data.verification_current || null,
      pending,
    );
    if (data.status === GS_STATUS_ACTIVE) {
      this.setFinished();
      this.tailPollsLeft = GS_TAIL_POLLS;
      this.scheduleNextPoll();
      return;
    }
    this.gsState.status = null;
    if (this.isFinished) {
      this.finishTaskBanner(cancelled, data);
      await this.loadVerificationHistory();
    }
    if (pending.length) {
      this.scheduleNextPoll();
      return;
    }
    if (this.tailPollsLeft > 0) {
      this.tailPollsLeft -= 1;
      this.scheduleNextPoll();
    }
  }

  public formatDate(value?: string | null): string {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    return date.toLocaleString('ru-RU');
  }

  public fullQrUrl(path: string): string {
    if (path.startsWith('http')) {
      return path;
    }
    return `${this.apiRoot}${path}`;
  }

  public async mounted() {
    await this.loadVerificationHistory();
    await this.checkGoogleSearchStatus();
  }

  public destroyed() {
    clearTimeout(this.timeOutPointer);
  }

  public async handleCancelTask() {
    this.gsState.status = null;
    this.tailPollsLeft = GS_TAIL_POLLS;
    const res = await Promise.all([
      dispatchCancelGoogleSearch(this.$store),
      delay(500),
    ]);
    if (res) {
      clearTimeout(this.timeOutPointer);
      this.checkGoogleSearchStatus(true);
    }
  }
}
